import type { FetchContext } from 'ofetch'

/**
 * Known retry reasons for common HTTP status codes and network errors.
 * Dynamic reasons like `http_${status}` or `network_error_${name}` are also possible.
 */
export type RetryReason =
  // HTTP status code reasons
  | 'rate_limited'
  | 'internal_error'
  | 'bad_gateway'
  | 'service_unavailable'
  | 'gateway_timeout'
  | '5xx_error'
  | '4xx_error'
  // Network error reasons
  | 'timeout'
  | 'connection_refused'
  | 'dns_failure'
  | 'connection_timeout'
  | 'connection_reset'
  | 'aborted'
  // Fallback
  | 'unknown'
  // Dynamic reasons (template literal types)
  | `http_${number}`
  | `network_error_${string}`

/**
 * Extract the reason for a retry from the fetch context.
 * This helps identify whether retries are due to network issues,
 * server errors, rate limiting, etc.
 */
export function getRetryReason(context: FetchContext): RetryReason {
  // Check for HTTP status codes first (more specific)
  if (context.response?.status) {
    const { status } = context.response

    // Map common retry-able status codes
    if (status === 429) return 'rate_limited'
    if (status === 500) return 'internal_error'
    if (status === 502) return 'bad_gateway'
    if (status === 503) return 'service_unavailable'
    if (status === 504) return 'gateway_timeout'

    // Generic status ranges
    if (status >= 500) return '5xx_error'
    if (status >= 400) return '4xx_error'

    return `http_${status}`
  }

  // Check for network/connection errors
  if (context.error) {
    const { error } = context
    const errorName = error.name || 'UnknownError'
    const errorMessage = (error.message || '').toLowerCase()

    // Check if error has a cause with the original error
    const causeMessage =
      error.cause instanceof Error
        ? (error.cause.message || '').toLowerCase()
        : ''

    // Combine both messages for checking
    const fullMessage = `${errorMessage} ${causeMessage}`

    // Common network errors (case-insensitive)
    if (fullMessage.includes('timeout') || errorName.includes('Timeout')) {
      return 'timeout'
    }
    if (fullMessage.includes('econnrefused')) {
      return 'connection_refused'
    }
    if (fullMessage.includes('enotfound')) {
      return 'dns_failure'
    }
    if (fullMessage.includes('etimedout')) {
      return 'connection_timeout'
    }
    if (fullMessage.includes('econnreset')) {
      return 'connection_reset'
    }
    if (errorName === 'AbortError') {
      return 'aborted'
    }

    return `network_error_${errorName.toLowerCase()}`
  }

  return 'unknown'
}
